/**
 * TRUS-709: pure helpers for the member Inbox — times, staff sender names and
 * the "someone else replied" signal. No React, no network.
 */
import type { Message } from '@/services/messagingApi';
import type { StaffReply } from '@/services/memberInboxApi';

/** React Query key prefix of the member Inbox list. */
export const MEMBER_INBOX_KEY = 'member-inbox';

type T = (key: string, fallback?: string, opts?: Record<string, unknown>) => string;

/**
 * Server timestamps are UTC, sometimes without an offset
 * (`timestamp without time zone`): read them as UTC, not local time.
 */
export function parseServerTime(iso: string | null | undefined): number {
  if (!iso) return NaN;
  const hasTz = /[zZ]$|[+-]\d{2}:?\d{2}$/.test(iso);
  return new Date(hasTz ? iso : `${iso}Z`).getTime();
}

/** "just now" / "2 min ago" / "3 h ago" / "4 d ago" / a date. */
export function relativeAgo(iso: string | null | undefined, t: T, now: number = Date.now()): string {
  const then = parseServerTime(iso);
  if (Number.isNaN(then)) return '';
  const mins = Math.max(0, Math.floor((now - then) / 60000));
  if (mins < 1) return t('messaging.inbox.justNow', 'just now');
  if (mins < 60) return t('messaging.inbox.minutesAgo', '{{count}} min ago', { count: mins });
  const hours = Math.floor(mins / 60);
  if (hours < 24) return t('messaging.inbox.hoursAgo', '{{count}} h ago', { count: hours });
  const days = Math.floor(hours / 24);
  if (days < 7) return t('messaging.inbox.daysAgo', '{{count}} d ago', { count: days });
  return new Date(then).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

/** Compact list time: "now", "5m", "3h", "2d", or a date. */
export function shortTime(iso: string | null | undefined, t: T, now: number = Date.now()): string {
  const then = parseServerTime(iso);
  if (Number.isNaN(then)) return '';
  const mins = Math.max(0, Math.floor((now - then) / 60000));
  if (mins < 1) return t('messaging.inbox.nowShort', 'now');
  if (mins < 60) return t('messaging.inbox.minutesShort', '{{count}}m', { count: mins });
  const hours = Math.floor(mins / 60);
  if (hours < 24) return t('messaging.inbox.hoursShort', '{{count}}h', { count: hours });
  const days = Math.floor(hours / 24);
  if (days < 7) return t('messaging.inbox.daysShort', '{{count}}d', { count: days });
  return new Date(then).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

export function initialsOf(name: string | null | undefined): string {
  const parts = (name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  return parts
    .slice(0, 2)
    .map((p) => p[0])
    .join('')
    .toUpperCase();
}

/** Placeholder names older rows carry instead of the person who sent them. */
const GENERIC_SENDER_NAMES = new Set(['', 'staff', 'system', 'unknown', 'weavein', 'null', 'undefined']);

export function isGenericSenderName(name: string | null | undefined): boolean {
  return GENERIC_SENDER_NAMES.has((name || '').trim().toLowerCase());
}

/**
 * Who sent a staff message in a member DM. Prefers the sender's own name, then
 * a name the thread can resolve from the sender's user id, then the staff user
 * a mirrored card / metadata names. `null` when no staff member is known (the
 * caller shows a neutral "Staff").
 */
export function resolveStaffSenderName(
  message: Pick<Message, 'sender' | 'metadata'>,
  nameOf?: (userId: number) => string | undefined,
): string | null {
  const own = message.sender?.name;
  if (!isGenericSenderName(own)) return own as string;
  const meta = (message.metadata || {}) as Record<string, any>;
  const ids = [message.sender?.user_id, meta.sender_user_id, meta.staff_user_id, meta.card?.staff_user_id];
  for (const id of ids) {
    if (typeof id === 'number' && nameOf) {
      const n = nameOf(id);
      if (n && !isGenericSenderName(n)) return n;
    }
  }
  for (const n of [meta.sender_name, meta.staff_name, meta.card?.staff_name]) {
    if (typeof n === 'string' && !isGenericSenderName(n)) return n;
  }
  return null;
}

/**
 * Whether a staff message's name shows. In a member DM it always does —
 * every staff message is attributed (the name shows whenever the sender changes
 * and on every message of a run, so a grouped run still says who wrote it).
 * Elsewhere the existing header rule applies (`fallback`).
 */
export function showStaffName(isMemberConversation: boolean, isMemberSender: boolean, fallback: boolean): boolean {
  if (isMemberConversation && !isMemberSender) return true;
  return fallback;
}

export interface OtherReply extends StaffReply {
  /** The message to scroll to, when the thread has it loaded. */
  message_id: number | null;
}

/** Staff messages (not mine, not the member's, not system / notes) sent after `sinceMs`. */
export function otherStaffRepliesFromMessages(
  messages: Message[],
  sinceMs: number,
  myUserId: number | null,
  nameOf?: (userId: number) => string | undefined,
): OtherReply[] {
  const latest = new Map<number, OtherReply>();
  for (const m of messages) {
    const uid = m.sender?.user_id;
    if (uid == null || uid === myUserId || m.sender?.member_id != null) continue;
    if (m.content_type === 'system') continue;
    const meta = (m.metadata || {}) as Record<string, any>;
    // A note is not a reply to the member; a bot post is not staff.
    if (meta.system_event || meta.internal_note === true) continue;
    const at = parseServerTime(m.created_at);
    if (Number.isNaN(at) || at <= sinceMs) continue;
    const prev = latest.get(uid);
    if (!prev || parseServerTime(prev.at) < at) {
      latest.set(uid, {
        user_id: uid,
        name: resolveStaffSenderName(m, nameOf) || '',
        at: m.created_at,
        message_id: m.id,
      });
    }
  }
  return [...latest.values()];
}

/**
 * Merge the server's `other_staff_replies_since` with what the thread sees in
 * its own messages: one entry per staff member, newest first.
 */
export function mergeOtherReplies(
  server: StaffReply[] | null | undefined,
  local: OtherReply[],
  sinceMs: number,
  myUserId: number | null,
): OtherReply[] {
  const byUser = new Map<number, OtherReply>();
  for (const r of local) byUser.set(r.user_id, r);
  for (const r of server || []) {
    if (r.user_id === myUserId) continue;
    const at = parseServerTime(r.at);
    if (!Number.isNaN(at) && at <= sinceMs) continue;
    const prev = byUser.get(r.user_id);
    if (!prev) byUser.set(r.user_id, { ...r, message_id: null });
    else if (!prev.name && r.name) byUser.set(r.user_id, { ...prev, name: r.name });
  }
  return [...byUser.values()].sort((a, b) => parseServerTime(b.at) - parseServerTime(a.at));
}
